/**
 * <ParentQuickActionsSection> — the four fixed gestures of the parent home (template).
 *
 * The list of actions is an INTERNAL const — labels, icons and order are the same on every
 * screen that mounts this block, and a call-site only supplies the handlers.
 *
 * **Verrou de vocabulaire** (Wajih, 28/09 — libellés à l'essai, sur les règles du 17/09). Chaque
 * mot se redit tel quel partout où le geste se nomme : titre du parcours, écran « Trop tard pour
 * décaler », accusé « Séance décalée », lignes de la feuille d'actions (« Décaler cette séance »,
 * « Écrire à Camille »), ligne du portail Abonnement.
 *  - « Décaler une séance » — jamais « la » séance ni « un cours » : il y en a plusieurs, et le
 *    parent choisit laquelle à l'étape suivante ;
 *  - « Signaler un retard » ;
 *  - « Écrire à un tuteur » — jamais « au » tuteur : une famille en a souvent plusieurs ;
 *  - « Réserver un cours en plus » — c'est un ACHAT, en plus de l'abonnement : ni « Ajouter », ni
 *    « Demander », qui annoncent une démarche sans prix.
 *
 * No section title and no subtitle under a button: the words carry the whole meaning.
 */
import React from "react"

import { type IconName } from "../../atoms/icons/Icon"
import { QuickActionRow } from "../../molecules/QuickActionRow"
import { PARENT_GESTURES } from "../../../ports/primary/ui/formatters/parentGestures"

export type ParentQuickActionsSectionProps = {
    /** « Décaler une séance » — opens the "Quelle séance ?" step. */
    onMoveSession?: () => void
    /** "Signaler un retard" — opens the "Quelle séance ?" step, late branch. */
    onReportLate?: () => void
    /** « Écrire à un tuteur » — opens "Quel tuteur ?", or the composer when there is only one. */
    onMessageTutor?: () => void
    /**
     * L'enfant a une séance à venir. Défaut `true`. Sans elle, les gestes d'une séance
     * (`PARENT_GESTURES.*.needsSession`) s'éteignent ; la tuile garde sa place.
     */
    hasUpcomingSession?: boolean
    /** « Réserver un cours en plus » — opens the extra-hours purchase (`<ExtraHoursFlow>`, or the legacy form). */
    onAddOneOffLesson?: () => void
    /** Greys out « Réserver un cours en plus » when the family has no active subscription. */
    addOneOffLessonDisabled?: boolean
    /**
     * Retire « Réserver un cours en plus » de la rangée (l'achat n'existe pas pour cette famille).
     * Défaut `false` : la tuile est là, active ou grisée.
     */
    addOneOffLessonHidden?: boolean
    testID?: string
}

type ActionKey = "onMoveSession" | "onReportLate" | "onMessageTutor" | "onAddOneOffLesson"

/**
 * Fixed rows: label + glyph, in this order, always. Un geste que la feuille d'actions d'une séance
 * offre aussi prend son glyphe — et son libellé quand les deux le disent pareil — dans
 * `PARENT_GESTURES`, qui dit aussi s'il attend une séance.
 */
const ACTIONS: { key: ActionKey; label: string; icon: IconName; needsSession: boolean }[] = [
    { key: "onMoveSession", label: "Décaler une séance", ...PARENT_GESTURES.move_session },
    { key: "onReportLate", ...PARENT_GESTURES.report_late },
    { key: "onMessageTutor", label: "Écrire à un tuteur", ...PARENT_GESTURES.message_tutor },
    { key: "onAddOneOffLesson", label: "Réserver un cours en plus", icon: "calendar-plus", needsSession: false },
]

export function ParentQuickActionsSection(props: ParentQuickActionsSectionProps) {
    const { hasUpcomingSession = true, addOneOffLessonDisabled, addOneOffLessonHidden = false, testID } = props
    const actions = addOneOffLessonHidden ? ACTIONS.filter((action) => action.key !== "onAddOneOffLesson") : ACTIONS

    return (
        <QuickActionRow
            testID={testID}
            actions={actions.map((action) => ({
                label: action.label,
                iconName: action.icon,
                onPress: props[action.key],
                disabled: action.needsSession ? !hasUpcomingSession : addOneOffLessonDisabled,
            }))}
        />
    )
}
